import type { FC, ReactNode } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import type { EmployeeMatchUserOption, EmployeeSourceRowMatchResult } from '../types'

interface SmartImportEmployeeMatchTableProps {
  rows: EmployeeSourceRowMatchResult[]
  userOptions: EmployeeMatchUserOption[]
  onAssignUser: (rowId: string, userId: string) => void
  onSkipRow: (rowId: string) => void
  title?: string
  description?: string
  footer?: ReactNode
}

const statusLabel: Record<EmployeeSourceRowMatchResult['status'], string> = {
  unresolved: 'Unresolved',
  matched: 'Matched',
  skipped: 'Skipped',
}

const getStatusColor = (status: EmployeeSourceRowMatchResult['status']): 'warning' | 'success' | 'default' => {
  if (status === 'matched') {
    return 'success'
  }

  if (status === 'unresolved') {
    return 'warning'
  }

  return 'default'
}

const formatUserOptionLabel = (option: EmployeeMatchUserOption): string => {
  return option.teamName ? `${option.name} (${option.teamName})` : option.name
}

const SmartImportEmployeeMatchTable: FC<SmartImportEmployeeMatchTableProps> = ({
  rows,
  userOptions,
  onAssignUser,
  onSkipRow,
  title = 'Review employee matches',
  description = 'Resolve unmatched rows before creating employee reward packages.',
  footer,
}) => {
  return (
    <Paper sx={{ p: 3, width: '100%' }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>Row</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Reward</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell width={280}>Assign user</TableCell>
                <TableCell align="right" width={120}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const assignedUserId = row.assignedUserId || row.matchedUser?.id || ''
                const isSkipped = row.status === 'skipped'
                const selectedUserOption = userOptions.find((userOption) => userOption.id === assignedUserId) || null

                return (
                  <TableRow key={row.rowId} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.sourceRow.rowNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{row.sourceRow.employeeName}</Typography>
                        {row.sourceRow.employeeEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {row.sourceRow.employeeEmail}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.sourceRow.rewardName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={getStatusColor(row.status)}
                        label={statusLabel[row.status]}
                        variant={row.status === 'skipped' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        options={userOptions}
                        value={selectedUserOption}
                        disabled={isSkipped}
                        autoHighlight
                        clearOnEscape
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={formatUserOptionLabel}
                        onChange={(_, newValue) => onAssignUser(row.rowId, newValue?.id || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Employee"
                            placeholder="Search employee"
                            variant="outlined"
                            shrink
                            pill
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: 'new-password',
                            }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        disabled={isSkipped}
                        onClick={() => onSkipRow(row.rowId)}
                      >
                        Skip
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {footer && <Box>{footer}</Box>}
      </Stack>
    </Paper>
  )
}

export default SmartImportEmployeeMatchTable
